import { Badge } from "@loot-os/ui/components/badge";

/** Veriff's session statuses (lowercased) as the badge shows them; mirrors `KYC_STATUSES` in core. */
const LABELS: Record<string, string> = {
  created: "started",
  started: "in progress",
  submitted: "pending",
  review: "in review",
  approved: "approved",
  declined: "declined",
  resubmission_requested: "resubmit",
  expired: "expired",
  abandoned: "abandoned",
};

const POLICY_TEXT: Record<string, string> = {
  age: "Approved by Veriff, but the document holder is under the minimum age.",
  country: "Approved by Veriff, but the document country is restricted.",
  duplicate: "Approved by Veriff, but another account was verified with the same identity.",
};

/**
 * One end user's identity-verification standing. `verified` is what matters for withdrawals;
 * an approved-but-unverified user failed the platform's policy, and the title says why.
 */
export function KycStatusBadge({
  status,
  verified,
  policyIssue,
  title,
}: {
  status: string | null;
  verified: boolean;
  policyIssue?: string | null;
  title?: string;
}) {
  if (verified) {
    return (
      <Badge variant="default" title={title ?? "Identity verified."}>
        verified
      </Badge>
    );
  }
  if (!status) {
    return (
      <Badge variant="outline" title={title ?? "Identity verification not started."}>
        not verified
      </Badge>
    );
  }
  if (status === "approved") {
    return (
      <Badge
        variant="secondary"
        title={title ?? (policyIssue ? POLICY_TEXT[policyIssue] : undefined)}
      >
        approved · {policyIssue ?? "policy"}
      </Badge>
    );
  }
  const variant =
    status === "declined" ? "destructive" : status === "review" ? "secondary" : "outline";
  return (
    <Badge variant={variant} title={title}>
      {LABELS[status] ?? status}
    </Badge>
  );
}
